<template>
  <div class="min-h-screen bg-gray-50">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="mb-6">
      <!-- Mobile: título + Histórico/Calendário na mesma linha; Nova Reserva / Novo Serviço abaixo -->
      <div class="flex flex-col gap-3 sm:hidden">
        <div class="flex items-center justify-between gap-3 min-w-0">
          <h1 class="text-2xl font-bold text-gray-900 truncate min-w-0 pr-1">
            {{ historyView ? 'Histórico' : 'Reservas' }}
          </h1>
          <button
            v-if="hasProperties && !historyView"
            type="button"
            @click="openHistoryView"
            class="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 text-gray-800 bg-white hover:bg-gray-50 shadow-sm text-sm font-medium"
          >
            <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Histórico</span>
          </button>
          <button
            v-else-if="historyView"
            type="button"
            @click="closeHistoryView"
            class="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-indigo-300 text-indigo-700 bg-white hover:bg-indigo-50 shadow-sm text-sm font-medium"
          >
            <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>Calendário</span>
          </button>
        </div>
        <div v-if="hasProperties && !historyView" class="flex gap-2 w-full">
          <button
            @click="openCreateModal('booking')"
            :disabled="properties.length === 0"
            :class="[
              'flex-1 min-w-0 px-3 py-2.5 rounded-lg transition flex items-center justify-center gap-2 shadow-sm text-sm font-medium',
              properties.length === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            ]"
          >
            <svg class="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span class="truncate">Nova Reserva</span>
          </button>
          <button
            @click="openCreateModal('cleaning')"
            :disabled="properties.length === 0"
            :class="[
              'flex-1 min-w-0 px-3 py-2.5 rounded-lg transition flex items-center justify-center gap-2 shadow-sm text-sm font-medium',
              properties.length === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-amber-500 text-white hover:bg-amber-600'
            ]"
          >
            <svg class="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span class="truncate">Novo Serviço</span>
          </button>
        </div>
      </div>

      <!-- Desktop (sm+): layout original — título e botões na mesma linha, como antes -->
      <div class="hidden sm:flex sm:flex-row sm:justify-between sm:items-center gap-3">
        <h1 class="text-2xl sm:text-3xl font-bold text-gray-900">
          {{ historyView ? 'Histórico de reservas' : 'Reservas' }}
        </h1>
        <div class="w-full sm:w-auto flex items-center gap-2 sm:gap-3 sm:justify-end flex-wrap">
          <template v-if="historyView">
            <button
              type="button"
              @click="closeHistoryView"
              class="flex-1 sm:flex-none px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg transition flex items-center justify-center space-x-2 shadow-sm text-sm sm:text-base border border-indigo-300 text-indigo-700 bg-white hover:bg-indigo-50"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>Calendário</span>
            </button>
          </template>
          <template v-else-if="hasProperties">
            <button
              @click="openCreateModal('booking')"
              :disabled="properties.length === 0"
              :class="[
                'flex-1 sm:flex-none px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg transition flex items-center justify-center space-x-2 shadow-sm text-sm sm:text-base',
                properties.length === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              ]"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>Nova Reserva</span>
            </button>

            <button
              @click="openCreateModal('cleaning')"
              :disabled="properties.length === 0"
              :class="[
                'flex-1 sm:flex-none px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg transition flex items-center justify-center space-x-2 shadow-sm text-sm sm:text-base',
                properties.length === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-amber-500 text-white hover:bg-amber-600'
              ]"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>Novo Serviço</span>
            </button>

            <button
              type="button"
              @click="openHistoryView"
              class="flex-1 sm:flex-none px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg transition flex items-center justify-center space-x-2 shadow-sm text-sm sm:text-base border border-gray-300 text-gray-800 bg-white hover:bg-gray-50"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Histórico</span>
            </button>
          </template>
        </div>
      </div>
      </div>

      <!-- Estado vazio: Sem imóveis cadastrados (só após carregar propriedades) -->
      <div v-if="noProperties" class="bg-white rounded-lg shadow-sm p-12">
        <div class="max-w-md mx-auto text-center">
          <div class="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-4">
            <svg class="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
          <h3 class="text-xl font-semibold text-gray-900 mb-2">Nenhum imóvel cadastrado</h3>
          <p class="text-gray-600 mb-6">
            Para criar reservas, é necessário cadastrar ao menos um imóvel no sistema.
          </p>
          <button
            @click="navigateToProperties"
            class="inline-flex items-center px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition shadow-sm"
          >
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Cadastrar Imóvel
          </button>
        </div>
      </div>

      <BookingHistory
        v-else-if="hasProperties && historyView"
        ref="bookingHistory"
        @edit-stay="editBooking"
      />

      <!-- Calendário e lista: quando há imóveis -->
      <div v-else-if="hasProperties && !historyView">
      <div class="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div class="mb-4 w-full md:w-80">
          <PropertySelectAsync
            v-model="selectedPropertyId"
            label="Filtrar por Imóvel"
            placeholder=""
            :required="true"
          />
        </div>

        <div class="flex items-center justify-center mb-4">
          <div class="flex items-center gap-3">
            <button
              @click="previousMonth"
              class="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <h2 class="text-xl font-semibold text-gray-900">
              {{ monthNames[currentMonth] }} {{ currentYear }}
            </h2>

            <button
              @click="nextMonth"
              class="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <button
              @click="goToToday"
              class="ml-4 text-sm px-3 py-1 bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 transition"
            >
              Hoje
            </button>
          </div>
        </div>

        <div class="relative">
          <!-- Overlay de loading no calendário -->
          <div v-if="loading" class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
            <div class="flex flex-col items-center">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-2"></div>
              <p class="text-sm text-gray-600">Atualizando calendário...</p>
            </div>
          </div>

          <div class="grid grid-cols-7 gap-2">
            <div
              v-for="day in weekDays"
              :key="day"
              class="text-center text-sm font-semibold text-gray-600 py-2"
            >
              {{ day }}
            </div>

            <div
              v-for="(day, index) in calendarDays"
              :key="index"
              :class="[
                'min-h-16 p-1.5 border border-gray-200 rounded-lg',
                day.isCurrentMonth ? 'bg-white' : 'bg-gray-50',
                day.isToday ? 'ring-2 ring-indigo-500' : ''
              ]"
            >
              <div class="flex justify-between items-start mb-1">
                <span
                  :class="[
                    'text-sm font-medium',
                    day.isCurrentMonth ? 'text-gray-900' : 'text-gray-400',
                    day.isToday ? 'bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs' : ''
                  ]"
                >
                  {{ day.date.getDate() }}
                </span>
              </div>

              <div class="space-y-1">
                <div
                  v-for="booking in getBookingsForDay(day.date)"
                  :key="booking.id"
                  @click="viewBooking(booking)"
                  :class="[
                    'text-xs p-1 rounded cursor-pointer truncate',
                    getBookingColor(booking)
                  ]"
                  :title="getBookingTooltip(booking)"
                >
                  {{ booking.customer?.name || (booking.type === 'Service' ? 'Prestador' : 'Hóspede') }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow-sm p-6">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Reservas do Mês</h3>
        
        <div v-if="loading" class="flex flex-col items-center justify-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-3"></div>
          <p class="text-sm text-gray-600">Carregando reservas...</p>
        </div>

        <div v-else-if="bookings.length === 0" class="text-center py-8 text-gray-500">
          Nenhuma reserva neste período
        </div>

        <div v-else class="space-y-3">
          <div
            v-for="booking in sortedBookings"
            :key="booking.id"
            @click="viewBooking(booking)"
            :class="[
              'p-4 rounded-lg border-l-4 cursor-pointer hover:shadow-md transition',
              booking.type === 'Service' ? 'bg-amber-50 border-amber-500' : 'bg-indigo-50 border-indigo-500'
            ]"
          >
            <div class="flex justify-between items-start">
              <div class="flex-1">
                <div class="flex items-center space-x-2 mb-2">
                  <!-- Ícone e tipo -->
                  <div :class="[
                    'flex items-center justify-center w-8 h-8 rounded-full',
                    booking.type === 'Service' ? 'bg-amber-200' : 'bg-indigo-200'
                  ]">
                    <svg v-if="booking.type === 'Service'" class="w-5 h-5 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <svg v-else class="w-5 h-5 text-indigo-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                  </div>
                  
                  <div class="flex-1">
                    <div class="flex items-center gap-2">
                      <h4 class="font-semibold text-gray-900">{{ booking.customer?.name || (booking.type === 'Service' ? 'Prestador' : 'Hóspede') }}</h4>
                      <span :class="[
                        'text-xs px-2 py-0.5 rounded-full font-medium',
                        booking.type === 'Service' ? 'bg-amber-100 text-amber-800 border border-amber-300' : 'bg-indigo-100 text-indigo-800 border border-indigo-300'
                      ]">
                        {{ booking.type === 'Service' ? 'Serviço' : 'Hospedagem' }}
                      </span>
                    </div>
                    <p class="text-xs text-gray-500 mt-0.5">{{ getBookingLabel(booking) }}</p>
                  </div>
                </div>
                
                <div class="ml-10">
                  <p class="text-sm text-gray-700 mb-2 font-medium">{{ booking.property?.name || 'Imóvel' }}</p>
                  <div class="flex items-center space-x-4 text-xs text-gray-600">
                    <span class="flex items-center gap-1">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {{ formatDate(booking.check_in_date) }}{{ booking.check_in_time ? ' às ' + formatTime(booking.check_in_time) : '' }}
                    </span>
                    <span class="text-gray-400">→</span>
                    <span class="flex items-center gap-1">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {{ formatDate(booking.check_out_date) }}{{ booking.check_out_time ? ' às ' + formatTime(booking.check_out_time) : '' }}
                    </span>
                    <span v-if="booking.total_price" class="ml-auto font-semibold text-gray-900">
                      R$ {{ parseFloat(booking.total_price).toFixed(2) }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="flex space-x-2">
                <button
                  @click.stop="editBooking(booking)"
                  class="text-indigo-600 hover:text-indigo-900"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  @click.stop="confirmDelete(booking)"
                  class="text-red-600 hover:text-red-900"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>

    <div
      v-if="isModalOpen"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
          <h2 class="text-xl font-semibold text-gray-900">
            {{ editingBooking ? (entryType === 'cleaning' ? 'Editar Serviço' : 'Editar Reserva') : (entryType === 'cleaning' ? 'Novo Serviço' : 'Nova Reserva') }}
          </h2>
          <button @click="closeModal" class="text-gray-400 hover:text-gray-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form @submit.prevent="openSaveConfirmBooking" class="px-6 py-4 overflow-y-auto flex-1 flex flex-col">
          <div v-if="entryType === 'cleaning'" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <PersonSelectAsync
                  v-model="form.customer_id"
                  :selected-person="selectedProvider"
                  label="Prestador Serviço *"
                  role="provider"
                  @update:modelValue="handleProviderChange"
                />
              </div>              

              <div class="grid-cols-1 md:grid-cols-2 gap-4">
                <PropertySelectAsync
                  v-model="form.property_id"
                  :selected-property="selectedFormProperty"
                  label="Imóvel *"
                  placeholder="Selecione um imóvel..."
                />
              </div>

              <div class="md:col-span-2">
                <ServiceTypeSelectAsync
                  v-model="form.service_type_id"
                  :selected-service-type="selectedFormServiceType"
                  label="Tipo de Serviço *"
                  placeholder="Selecione um tipo de serviço..."
                  @update:modelValue="handleServiceTypeChange"
                />
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Data Início *</label>
                <input
                  v-model="form.check_in_date"
                  type="date"
                  required
                  class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  <span class="flex items-center gap-1">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Horário Início
                  </span>
                </label>
                <input
                  v-model="form.check_in_time"
                  type="time"
                  class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Data Fim *</label>
                <input
                  v-model="form.check_out_date"
                  type="date"
                  required
                  class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  <span class="flex items-center gap-1">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Horário Fim
                  </span>
                </label>
                <input
                  v-model="form.check_out_time"
                  type="time"
                  class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <!-- Aviso para mesmo dia -->
            <div v-if="formErrors.sameDayCheckout" class="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
              <div class="flex">
                <div class="flex-shrink-0">
                  <svg class="h-5 w-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div class="ml-3">
                  <p class="text-sm text-yellow-700">{{ formErrors.sameDayCheckout }}</p>
                </div>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Valor Total a Pagar (R$)</label>
              <input
                v-model="form.total_payable"
                type="text"
                @input="e => handleCurrencyInput(e, 'total_payable')"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Observação</label>
              <textarea
                v-model="form.description"
                rows="4"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              ></textarea>
            </div>

            <!-- Anexos (serviços) -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Anexos</label>
              <AttachmentManager
                v-model="form.attachments"
                v-model:remove-attachment-ids="form.remove_attachment_ids"
                v-model:attachments-order="form.attachments_order"
                :accepted-types="'*'"
                :enable-reorder="true"
                :enable-lightbox="true"
              />
            </div>
          </div>

          <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <PersonSelectAsync
                v-model="form.customer_id"
                :selected-person="selectedCustomer"
                label="Hóspede *"
                role="customer"
                @update:modelValue="handleCustomerChange"
              />
            </div>

            <div>
              <PropertySelectAsync
                v-model="form.property_id"
                :selected-property="selectedFormProperty"
                label="Imóvel *"
                placeholder="Selecione um imóvel..."
              />
            </div>

            <div v-if="entryType === 'booking' && !editingBooking" class="md:col-span-2">
              <div v-if="loadingLastHostEvaluation" class="text-xs text-gray-500">
                Buscando última avaliação desse hóspede...
              </div>
              <div
                v-else-if="lastHostEvaluation"
                class="bg-white border border-gray-200 rounded-lg p-4"
              >
                <div class="text-sm font-semibold text-gray-900 mb-2">Última avaliação desse hóspede</div>
                <div class="flex items-start gap-3 flex-wrap">
                  <div class="flex text-lg leading-none" aria-label="Últimas estrelas avaliadas pelo anfitrião">
                    <span
                      v-for="n in 5"
                      :key="'last-host-' + n"
                      :class="n <= Number(lastHostEvaluation.host_stars || 0) ? 'text-amber-500' : 'text-gray-300'"
                    >
                      ★
                    </span>
                  </div>

                  <div class="text-sm text-gray-900 font-semibold">
                    {{ Number(lastHostEvaluation.host_stars || 0) }}/5
                    <span v-if="lastHostEvaluation.host_evaluated_at">
                      ({{ formatDateTime(lastHostEvaluation.host_evaluated_at) }})
                    </span>
                    <span v-if="lastHostEvaluation.property?.name">
                      - {{ lastHostEvaluation.property.name }}
                    </span>
                  </div>
                </div>

                <div v-if="lastHostEvaluation.host_comment" class="mt-2 text-gray-700 whitespace-pre-wrap">
                  {{ lastHostEvaluation.host_comment }}
                </div>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Check-in *</label>
              <input
                v-model="form.check_in_date"
                type="date"
                required
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                <span class="flex items-center gap-1">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Horário Check-in
                </span>
              </label>
              <input
                v-model="form.check_in_time"
                type="time"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Check-out *</label>
              <input
                v-model="form.check_out_date"
                type="date"
                required
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                <span class="flex items-center gap-1">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Horário Check-out
                </span>
              </label>
              <input
                v-model="form.check_out_time"
                type="time"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <!-- Aviso para mesmo dia -->
            <div v-if="formErrors.sameDayCheckout" class="md:col-span-2 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
              <div class="flex">
                <div class="flex-shrink-0">
                  <svg class="h-5 w-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div class="ml-3">
                  <p class="text-sm text-yellow-700">{{ formErrors.sameDayCheckout }}</p>
                </div>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Número de Hóspedes</label>
              <input
                v-model.number="form.number_of_guests"
                type="number"
                min="1"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Valor Total a Receber (R$)</label>
              <input
                v-model="form.total_due"
                type="text"
                @input="e => handleCurrencyInput(e, 'total_due')"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Valor Sinal (R$)</label>
              <input
                v-model="form.deposit_amount"
                type="text"
                @input="e => handleCurrencyInput(e, 'deposit_amount')"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Valor Final (R$)</label>
              <input
                v-model="form.final_amount"
                type="text"
                @input="e => handleCurrencyInput(e, 'final_amount')"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div class="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Saldo a Receber (R$)</label>
                <input
                  :value="formatCurrencyDisplay(calculateBalanceDue())"
                  type="text"
                  disabled
                  class="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed focus:outline-none"
                />
                <p class="text-xs text-gray-500 mt-1">Calculado automaticamente: Valor Total - Sinal - Valor Final</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Condições de pagamento</label>
                <textarea
                  v-model="form.payment_conditions"
                  rows="3"
                  class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[5.5rem]"
                  placeholder="Ex.: 4 parcelas de R$ 500,00, de janeiro a abril"
                ></textarea>
              </div>
            </div>

            <div class="md:col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-1">Observação do Hóspede</label>
              <textarea
                v-model="form.guest_note"
                rows="3"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              ></textarea>
            </div>

            <div>
              <PersonSelectAsync
                v-model="form.seller_id"
                :selected-person="selectedSeller"
                label="Corretor"
                role="agent"
                @update:modelValue="handleSellerChange"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Valor Total a Pagar (R$)</label>
              <input
                v-model="form.total_payable"
                type="text"
                @input="e => handleCurrencyInput(e, 'total_payable')"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Valor Total Pago (R$)</label>
              <input
                v-model="form.total_paid"
                type="text"
                @input="e => handleCurrencyInput(e, 'total_paid')"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Saldo a Pagar (R$)</label>
              <input
                :value="formatCurrencyDisplay(calculateBalancePayable())"
                type="text"
                disabled
                class="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed focus:outline-none"
              />
              <p class="text-xs text-gray-500 mt-1">Calculado automaticamente: Total a Pagar - Total Pago</p>
            </div>

            <div class="md:col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-1">Observação do Corretor</label>
              <textarea
                v-model="form.seller_note"
                rows="3"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              ></textarea>
            </div>

            <!-- Anexos -->
            <div class="md:col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-1">Anexos da reserva</label>
              <p class="text-xs text-gray-500 mb-2">
                Recibos, fotos ou outros documentos relevantes para a reserva.
              </p>
              <AttachmentManager
                v-model="form.attachments"
                v-model:remove-attachment-ids="form.remove_attachment_ids"
                v-model:attachments-order="form.attachments_order"
                :accepted-types="'*'"
                :enable-reorder="true"
                :enable-lightbox="true"
              />
            </div>

            <!-- Contrato: prévia, link público, PDF e registo de assinatura (reservas Stay) -->
            <div
              v-if="entryType === 'booking' && editingBooking && editingBooking.type !== 'Service'"
              class="md:col-span-2 rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden mt-2"
            >
              <div class="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50/80">
                <h4 class="text-sm font-semibold text-slate-800 tracking-wide">
                  Contrato
                </h4>
                <span
                  v-if="stayContractStatusBadgeLabel"
                  class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                  :class="stayContractStatusBadgeClass"
                >
                  {{ stayContractStatusBadgeLabel }}
                </span>
              </div>

              <div class="p-4 space-y-4">
                <p
                  v-if="stayContractUiStatus === 'no_template'"
                  class="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5"
                >
                  Cadastre um modelo de contrato (DOCX) no imóvel para habilitar prévia, link de assinatura e PDF
                  final.
                </p>

                <p
                  v-else-if="stayContractUiStatus === 'draft'"
                  class="text-sm text-slate-600 leading-relaxed"
                >
                  Baixe a <strong>prévia em Word</strong> para conferir os dados. Depois use
                  <strong>Gerar contrato final</strong> para criar o PDF e o link para enviar ao hóspede
                  assinar.
                </p>

                <p
                  v-else-if="stayContractUiStatus === 'sent'"
                  class="text-sm text-slate-600"
                >
                  Contrato final gerado, copiar o link e enviar ao hóspede para assinatura digital
                </p>

                <div
                  v-if="editingBooking.contract_signed_at"
                  class="rounded-lg border border-emerald-200 bg-emerald-50/90 text-emerald-950 px-4 py-4 text-sm"
                >
                  <div
                    v-if="editingBooking.contract_signing_generated_at || contractSigningGeneratedByLabel"
                    class="mb-4 pb-4 border-b border-emerald-200/70"
                  >
                    <p class="text-xs font-semibold uppercase tracking-wide text-emerald-800/80">
                      Contrato gerado
                    </p>
                    <p class="mt-1 text-sm text-emerald-950">
                      <template v-if="editingBooking.contract_signing_generated_at">
                        {{ formatDateTime(editingBooking.contract_signing_generated_at) }}
                      </template>
                      <template v-if="contractSigningGeneratedByLabel">
                        <span v-if="editingBooking.contract_signing_generated_at" class="text-emerald-700/80"> · </span>
                        {{ contractSigningGeneratedByLabel }}
                      </template>
                    </p>
                  </div>
                  <p class="font-semibold text-emerald-900">
                    Registo de assinatura
                  </p>
                  <dl class="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                    <div>
                      <dt class="text-xs font-medium uppercase tracking-wide text-emerald-800/80">
                        Data
                      </dt>
                      <dd class="mt-0.5 font-medium text-emerald-950">
                        {{ contractSignedDateLabel }}
                      </dd>
                    </div>
                    <div>
                      <dt class="text-xs font-medium uppercase tracking-wide text-emerald-800/80">
                        Hora
                      </dt>
                      <dd class="mt-0.5 font-medium text-emerald-950">
                        {{ contractSignedTimeLabel }}
                      </dd>
                    </div>
                    <div v-if="editingBooking.contract_signed_ip" class="sm:col-span-2">
                      <dt class="text-xs font-medium uppercase tracking-wide text-emerald-800/80">
                        Endereço IP
                      </dt>
                      <dd class="mt-0.5">
                        <code class="text-xs sm:text-sm bg-emerald-100/80 px-2 py-1 rounded border border-emerald-200/80 text-emerald-900">
                          {{ editingBooking.contract_signed_ip }}
                        </code>
                      </dd>
                    </div>
                  </dl>
                  <p v-if="editingBooking.contract_signing_pdf_url" class="mt-3 pt-2 border-t border-emerald-200/60">
                    <a
                      :href="editingBooking.contract_signing_pdf_url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-sm font-medium text-emerald-800 hover:text-emerald-950 underline"
                    >Visualizar PDF do contrato assinado</a>
                    <span class="text-xs text-emerald-700/90 ml-1"></span>
                  </p>
                </div>

                <div
                  v-if="editingBooking.contract_template_available && !editingBooking.contract_signed_at && !stayContractFinalBlockVisible"
                  class="flex flex-wrap gap-2"
                >
                  <button
                    type="button"
                    @click="exportContract"
                    :disabled="contractDownloading"
                    class="px-4 py-2 border border-slate-300 text-slate-800 bg-white rounded-lg hover:bg-slate-50 transition disabled:opacity-50 text-sm font-medium"
                  >
                    {{ contractDownloading ? 'Baixando…' : 'Download prévia do contrato' }}
                  </button>
                </div>

                <template v-if="!editingBooking.contract_signed_at">
                  <div
                    v-if="stayContractFinalBlockVisible"
                    class="rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3 space-y-3"
                  >
                    <div class="flex flex-wrap items-baseline justify-between gap-2">
                      <p class="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                        Contrato final
                      </p>
                      <p v-if="editingBooking.contract_signing_generated_at" class="text-xs text-slate-500">
                        Gerado {{ formatDateTime(editingBooking.contract_signing_generated_at) }}
                        <template v-if="contractSigningGeneratedByLabel">
                          <span class="text-slate-400">·</span> {{ contractSigningGeneratedByLabel }}
                        </template>
                      </p>
                    </div>
                    <div v-if="editingBooking.contract_signing_url">
                      <label class="block text-xs font-medium text-slate-600 mb-1.5">Link para o hóspede assinar</label>
                      <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                        <input
                          :value="editingBooking.contract_signing_url"
                          readonly
                          class="flex-1 px-3 py-2 border border-slate-300 rounded-lg bg-white text-slate-800 text-sm font-mono"
                        />
                        <div class="flex flex-wrap gap-2 shrink-0">
                          <button
                            type="button"
                            @click="copyContractSigningLink"
                            class="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition shadow-sm text-sm font-medium"
                          >
                            {{ contractSigningLinkCopied ? 'Copiado!' : 'Copiar' }}
                          </button>
                          <button
                            type="button"
                            class="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition shadow-sm text-sm font-medium"
                            title="Abre o WhatsApp com a mensagem e o link. Se o hóspede não tiver celular válido no cadastro, você escolhe o contato."
                            @click="shareContractSigningViaWhatsApp"
                          >
                            <i class="fab fa-whatsapp text-lg" aria-hidden="true"></i>
                            WhatsApp
                          </button>
                        </div>
                      </div>
                    </div>
                    <p v-if="editingBooking.contract_signing_pdf_url" class="text-sm">
                      <a
                        :href="editingBooking.contract_signing_pdf_url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-indigo-600 hover:text-indigo-800 font-medium"
                      >Visualizar PDF do contrato gerado</a>
                    </p>
                    <div class="flex flex-wrap items-start gap-2 pt-2 border-t border-slate-200/80">
                      <button
                        type="button"
                        @click="openContractSigningDeleteConfirm"
                        :disabled="contractSigningCancelling"
                        class="px-3 py-1.5 text-sm font-medium text-red-800 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition disabled:opacity-50 shrink-0"
                      >
                        {{ contractSigningCancelling ? 'Excluindo…' : 'Excluir contrato gerado' }}
                      </button>
                    </div>
                  </div>

                  <div v-if="showStayContractGenerateButton">
                    <button
                      type="button"
                      @click="generateContractSigning"
                      :disabled="contractSigningGenerating"
                      class="px-4 py-2 border border-indigo-200 text-indigo-800 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition disabled:opacity-50 text-sm font-medium"
                    >
                      {{ contractSigningGenerating ? 'Gerando…' : 'Gerar contrato final' }}
                    </button>
                  </div>
                </template>
              </div>
            </div>

            <!-- Avaliação pública do hóspede (somente para reservas Stay) -->
            <div
              v-if="entryType === 'booking' && editingBooking && editingBooking.type !== 'Service'"
              class="md:col-span-2"
            >
              <h4 class="text-sm font-semibold text-blue-700 uppercase tracking-wide mt-2 mb-3 pb-2 border-b border-blue-200">
                Avaliação do hóspede
              </h4>
              <label v-if="editingBooking.guest_evaluation_token" class="block text-sm font-medium text-gray-700 mb-1">
                Link para avaliação do hóspede
              </label>
              <div v-if="editingBooking.guest_evaluation_token" class="flex flex-col sm:flex-row sm:items-center gap-2">
                <input
                  :value="guestEvaluationLink"
                  readonly
                  class="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                />
                <div class="flex flex-wrap gap-2 shrink-0">
                  <button
                    type="button"
                    @click="copyGuestEvaluationLink"
                    class="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50"
                    :disabled="!guestEvaluationLink"
                  >
                    {{ guestLinkCopied ? 'Copiado!' : 'Copiar' }}
                  </button>
                  <button
                    type="button"
                    class="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition shadow-sm text-sm font-medium disabled:opacity-50"
                    title="Abre o WhatsApp com a mensagem e o link. Se o hóspede não tiver celular válido no cadastro, você escolhe o contato."
                    :disabled="!guestEvaluationLink"
                    @click="shareGuestEvaluationViaWhatsApp"
                  >
                    <i class="fab fa-whatsapp text-lg" aria-hidden="true"></i>
                    WhatsApp
                  </button>
                </div>
              </div>
              <p v-if="editingBooking.guest_evaluation_token" class="text-xs text-gray-500 mt-2">
                Disponível após o check-in até 7 dias após o check-out.
              </p>

              <!-- Mostra a avaliação desta reserva, no mesmo formato do detalhe do imóvel -->
              <div
                v-if="hasGuestEvaluation(editingBooking)"
                class="mt-4 bg-white border border-gray-200 rounded-lg p-4"
              >
                <div class="text-sm font-semibold text-gray-900 mb-2">Avaliação do hóspede</div>
                <div class="flex items-start gap-3 flex-wrap">
                  <div class="flex text-lg leading-none" aria-label="Estrelas avaliadas">
                    <span
                      v-for="n in 5"
                      :key="n"
                      :class="n <= Number(editingBooking.guest_stars || 0) ? 'text-amber-500' : 'text-gray-300'"
                    >
                      ★
                    </span>
                  </div>

                  <div class="text-sm text-gray-900 font-semibold">
                    {{ Number(editingBooking.guest_stars || 0) }}/5
                    <span v-if="editingBooking.guest_name">
                      ({{ editingBooking.guest_name }} - {{ formatDateTime(editingBooking.guest_evaluated_at) }})
                    </span>
                    <span v-else>
                      ({{ formatDateTime(editingBooking.guest_evaluated_at) }})
                    </span>
                  </div>
                </div>

                <div v-if="editingBooking.guest_comment" class="mt-2 text-gray-700 whitespace-pre-wrap">
                  {{ editingBooking.guest_comment }}
                </div>
              </div>

              <h4 class="text-sm font-semibold text-blue-700 uppercase tracking-wide mt-6 mb-3 pb-2 border-b border-blue-200">
                Avaliação do anfitrião
              </h4>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Estrelas</label>
                  <div class="inline-flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    <button
                      v-for="n in 5"
                      :key="'host-stars-' + n"
                      type="button"
                      @click="form.host_stars = n"
                      class="text-2xl leading-none transition-transform hover:scale-110"
                      :class="n <= Number(form.host_stars || 0) ? 'text-amber-500' : 'text-gray-300'"
                      :aria-label="`${n} estrela(s)`"
                    >
                      ★
                    </button>
                    <button
                      v-if="form.host_stars"
                      type="button"
                      class="ml-2 text-xs text-gray-500 hover:text-gray-700 underline"
                      @click="clearHostEvaluationFields"
                    >
                      Limpar
                    </button>
                  </div>
                </div>

                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Comentário</label>
                  <textarea
                    v-model="form.host_comment"
                    rows="3"
                    :disabled="!form.host_stars"
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    :class="!form.host_stars ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : ''"
                    :placeholder="form.host_stars ? 'Como foi a experiência com o hóspede?' : 'Selecione ao menos 1 estrela para comentar'"
                  ></textarea>
                </div>
              </div>

            </div>
          </div>
        </form>

        <div class="px-6 py-4 border-t border-gray-200 flex-shrink-0 bg-white flex justify-between items-center relative">
          <div v-if="formErrors.general" class="absolute left-6 -top-12 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg w-[calc(100%-3rem)] flex items-start justify-between">
            <span class="flex-1">{{ formErrors.general }}</span>
            <button 
              @click="formErrors.general = ''"
              class="ml-3 text-red-700 hover:text-red-900 flex-shrink-0"
              type="button"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <button
            v-if="editingBooking"
            type="button"
            @click="deleteConfirmBooking = editingBooking"
            class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            Excluir
          </button>
          <div v-else></div>
          <div class="flex flex-wrap items-center justify-end gap-2">
            <button
              v-if="editingBooking && entryType !== 'cleaning' && editingBooking.type === 'Service'"
              type="button"
              @click="exportContract"
              :disabled="contractDownloading"
              class="px-4 py-2 border border-indigo-200 text-indigo-700 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition disabled:opacity-50"
            >
              {{ contractDownloading ? 'Gerando contrato...' : 'Exportar contrato' }}
            </button>
            <button
              type="button"
              @click="openSaveConfirmBooking"
              :disabled="saving"
              class="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50"
            >
              {{ saving ? 'Salvando...' : 'Salvar' }}
            </button>
            <button
              type="button"
              @click="closeModal"
              class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
            >
              Sair
            </button>            
          </div>
        </div>
      </div>
    </div>

    <ConfirmationModal
      :isOpen="!!deleteConfirmBooking"
      title="Confirmar Exclusão"
      message="Tem certeza que deseja excluir esta reserva? Esta ação não pode ser desfeita."
      :loading="deleting"
      @confirm="deleteBooking"
      @cancel="deleteConfirmBooking = null"
    />

    <ConfirmationModal
      :isOpen="contractSigningDeleteConfirmOpen"
      title="Confirmar Exclusão"
      message="Tem certeza que deseja excluir o contrato gerado? Caso tenha enviado o link para o hóspede assinar, ele deixará de funcionar."
      :loading="contractSigningCancelling"
      @confirm="confirmDeleteContractSigning"
      @cancel="contractSigningDeleteConfirmOpen = false"
    />

    <ConfirmationModal
      :isOpen="saveConfirmBookingOpen"
      :title="entryType === 'cleaning' ? 'Confirmação' : 'Confirmação'"
      :message="entryType === 'cleaning' ? 'Deseja salvar este serviço?' : 'Deseja salvar esta reserva?'"
      :loading="saving"
      confirmLabel="Salvar"
      cancelLabel="Cancelar"
      confirmButtonColor="blue"
      @confirm="confirmSaveBooking"
      @cancel="closeSaveConfirmBooking"
    />

    <QuickPersonModal
      :isOpen="quickPersonModalOpen.isOpen"
      :personType="quickPersonModalOpen.type"
      @save="handleQuickPersonSave"
      @close="closeQuickPersonModal"
    />

    <QuickServiceTypeModal
      :isOpen="quickServiceTypeModalOpen"
      @save="handleQuickServiceTypeSave"
      @close="closeQuickServiceTypeModal"
    />
  </div>
</template>

<script>
import axios from 'axios'
import { router, navigateTo } from '../router.js'
import QuickPersonModal from './QuickPersonModal.vue'
import QuickServiceTypeModal from './QuickServiceTypeModal.vue'
import ConfirmationModal from './ConfirmationModal.vue'
import BookingHistory from './BookingHistory.vue'
import PersonSelect from './PersonSelect.vue'
import PersonSelectAsync from './PersonSelectAsync.vue'
import PropertySelectAsync from './PropertySelectAsync.vue'
import ServiceTypeSelect from './ServiceTypeSelect.vue'
import ServiceTypeSelectAsync from './ServiceTypeSelectAsync.vue'
import AttachmentManager from './AttachmentManager.vue'
import { openWhatsAppShare, buildWhatsAppMessageWithLink } from '../composables/useWhatsAppShare.js'
import { useBrazilianMasks } from '../composables/useBrazilianMasks.js'
import { useApi } from '../composables/useApi.js'
import { BRAZILIAN_STATES } from '../constants/brazilianStates.js'

export default {
  components: {
    QuickPersonModal,
    QuickServiceTypeModal,
    ConfirmationModal,
    BookingHistory,
    PersonSelect,
    PersonSelectAsync,
    PropertySelectAsync,
    ServiceTypeSelect,
    ServiceTypeSelectAsync,
    AttachmentManager
  },
  data() {
    const { applyCurrencyMask, parseCurrencyToNumber, getWhatsAppLink } = useBrazilianMasks()

    const initialYear = router.params?.year !== undefined ? Number(router.params.year) : new Date().getFullYear()
    const initialMonth = router.params?.monthIndex !== undefined ? Number(router.params.monthIndex) : new Date().getMonth()
    const masterCode = localStorage.getItem('masterCode') || 'default'
    const lastPropertyKey = `tripday:last_selected_property_id:${masterCode}`
    const savedPropertyId = localStorage.getItem(lastPropertyKey)
    const initialPropertyId =
      router.params?.propertyId !== undefined
        ? router.params.propertyId
        : (savedPropertyId !== null && savedPropertyId !== '' ? savedPropertyId : null)

    return {
      applyCurrencyMask,
      parseCurrencyToNumber,
      getWhatsAppLink,
      currentMonth: Number.isFinite(initialMonth) ? initialMonth : new Date().getMonth(),
      currentYear: Number.isFinite(initialYear) ? initialYear : new Date().getFullYear(),
      bookings: [],
      loading: false,
      isModalOpen: false,
      editingBooking: null,
      entryType: 'booking',
      selectedPropertyId: initialPropertyId,
      properties: [],
      propertiesLoaded: false,
      serviceTypes: [],
      saving: false,
      saveConfirmBookingOpen: false,
      deleteConfirmBooking: null,
      deleting: false,
      contractDownloading: false,
      contractSigningGenerating: false,
      contractSigningCancelling: false,
      contractSigningDeleteConfirmOpen: false,
      contractSigningLinkCopied: false,
      formErrors: {},
      form: this.getEmptyForm(),
      quickPersonModalOpen: {
        isOpen: false,
        type: null
      },
      quickServiceTypeModalOpen: false,
      stateOptions: BRAZILIAN_STATES,
      monthNames: [
        'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
      ],
      weekDays: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
      // Estados para armazenar os objetos selecionados
      currentCustomer: null,
      currentSeller: null,
      guestLinkCopied: false,
      lastHostEvaluation: null,
      loadingLastHostEvaluation: false,
      historyView: false
    }
  },

  computed: {
    stayIdToEdit() {
      return router.params?.stayId
    },

    hasProperties() {
      return Array.isArray(this.properties) && this.properties.length > 0
    },

    noProperties() {
      return this.propertiesLoaded && !this.hasProperties
    },

    calendarDays() {
      const firstDay = new Date(this.currentYear, this.currentMonth, 1)
      const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0)
      const startDate = new Date(firstDay)
      startDate.setDate(startDate.getDate() - firstDay.getDay())
      
      const days = []
      const endDate = new Date(lastDay)
      endDate.setDate(endDate.getDate() + (6 - lastDay.getDay()))

      const current = new Date(startDate)
      while (current <= endDate) {
        days.push({
          date: new Date(current),
          isCurrentMonth: current.getMonth() === this.currentMonth,
          isToday: this.isToday(current)
        })
        current.setDate(current.getDate() + 1)
      }

      return days
    },

    sortedBookings() {
      let filtered = this.bookings
      if (this.selectedPropertyId !== null && this.selectedPropertyId !== undefined) {
        filtered = filtered.filter(b => {
          const bookingPropertyId = b.property?.id || b.property_id
          return String(bookingPropertyId) === String(this.selectedPropertyId)
        })
      }
      return [...filtered].sort((a, b) => {
        const dateA = a.check_in_date.split('-').map(Number)
        const dateB = b.check_in_date.split('-').map(Number)
        const timeA = new Date(dateA[0], dateA[1] - 1, dateA[2]).getTime()
        const timeB = new Date(dateB[0], dateB[1] - 1, dateB[2]).getTime()
        return timeA - timeB
      })
    },

    selectedCustomer() {
      // Priorizar o estado atual (quando selecionado manualmente)
      if (this.currentCustomer) return this.currentCustomer
      // Fallback para editingBooking
      if (!this.editingBooking || !this.editingBooking.customer) return null
      return this.editingBooking.customer
    },

    selectedProvider() {
      // Para serviços, o prestador é representado como "customer" com role provider
      if (this.currentCustomer) return this.currentCustomer
      if (!this.editingBooking || !this.editingBooking.customer) return null
      return this.editingBooking.customer
    },

    selectedSeller() {
      // Priorizar o estado atual (quando selecionado manualmente)
      if (this.currentSeller) return this.currentSeller
      // Fallback para editingBooking
      if (!this.editingBooking || !this.editingBooking.seller) return null
      return this.editingBooking.seller
    },

    selectedFormProperty() {
      if (!this.editingBooking || !this.editingBooking.property) return null
      return this.editingBooking.property
    },

    selectedFormServiceType() {
      if (!this.editingBooking || !this.editingBooking.service_type) return null
      return this.editingBooking.service_type
    },

    serviceTypeOptions() {
      return [
        { value: 'new', label: '+ Novo Tipo de Serviço' },
        ...(Array.isArray(this.serviceTypes) ? this.serviceTypes.map(st => ({ value: String(st.id), label: st.name })) : [])
      ]
    },

    guestEvaluationLink() {
      if (!this.editingBooking?.guest_evaluation_token) return ''
      return `${window.location.origin}/public/review/${this.editingBooking.guest_evaluation_token}`
    },

    showStayContractGenerateButton() {
      const b = this.editingBooking
      if (!b || b.type === 'Service') return false
      return !!b.contract_template_available && !b.contract_signed_at && !this.stayContractFinalBlockVisible
    },

    /** Contrato final já existe (PDF ou registo de geração), ainda sem assinatura do hóspede. */
    stayContractFinalBlockVisible() {
      const b = this.editingBooking
      if (!b || b.type === 'Service' || b.contract_signed_at) return false
      return !!(b.contract_signing_generated_at || b.contract_signing_pdf_url)
    },

    contractSigningGeneratedByLabel() {
      const u = this.editingBooking?.contract_signing_generated_by
      if (!u) return ''
      return u.name || u.email || ''
    },

    /** 'no_template' | 'draft' | 'sent' | 'signed' */
    stayContractUiStatus() {
      const b = this.editingBooking
      if (!b || b.type === 'Service') return null
      if (!b.contract_template_available) return 'no_template'
      if (b.contract_signed_at) return 'signed'
      if (b.contract_signing_generated_at || b.contract_signing_pdf_url) return 'sent'
      return 'draft'
    },

    stayContractStatusBadgeClass() {
      switch (this.stayContractUiStatus) {
        case 'no_template':
          return 'bg-amber-100 text-amber-900 ring-1 ring-inset ring-amber-200'
        case 'draft':
          return 'bg-slate-100 text-slate-800 ring-1 ring-inset ring-slate-200'
        case 'sent':
          return 'bg-indigo-100 text-indigo-900 ring-1 ring-inset ring-indigo-200'
        case 'signed':
          return 'bg-emerald-100 text-emerald-900 ring-1 ring-inset ring-emerald-200'
        default:
          return 'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200'
      }
    },

    stayContractStatusBadgeLabel() {
      switch (this.stayContractUiStatus) {
        case 'no_template':
          return 'Sem modelo no imóvel'
        case 'draft':
          return 'Só prévia'
        case 'sent':
          return 'Aguardando assinatura'
        case 'signed':
          return 'Assinado'
        default:
          return ''
      }
    },

    contractSignedDateLabel() {
      const s = this.editingBooking?.contract_signed_at
      if (!s) return ''
      const d = new Date(s)
      if (Number.isNaN(d.getTime())) return ''
      return d.toLocaleDateString('pt-BR')
    },

    contractSignedTimeLabel() {
      const s = this.editingBooking?.contract_signed_at
      if (!s) return ''
      const d = new Date(s)
      if (Number.isNaN(d.getTime())) return ''
      return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    }
  },

  watch: {
    stayIdToEdit(newVal) {
      if (newVal) {
        const booking = this.bookings.find(b => b.id === newVal)
        if (booking) {
          this.editBooking(booking)
          // Limpar o parâmetro para próximas navegações
          router.params.stayId = null
        }
      }
    },

    selectedPropertyId(newVal, oldVal) {
      // Quando o imóvel selecionado mudar, recarregar os dados
      if (!this.historyView && newVal !== oldVal && oldVal !== undefined) {
        this.loadServicesByMonth()
      }

      const masterCode = localStorage.getItem('masterCode') || 'default'
      const lastPropertyKey = `tripday:last_selected_property_id:${masterCode}`
      if (newVal === null || newVal === undefined || newVal === '') {
        localStorage.removeItem(lastPropertyKey)
      } else {
        localStorage.setItem(lastPropertyKey, String(newVal))
      }
    },

    currentMonth(newVal) {
      if (!this.historyView) this.loadServicesByMonth()
    },

    currentYear(newVal) {
      if (!this.historyView) this.loadServicesByMonth()
    },

    'form.attachments': {
      handler(newVal) {
        // Atualizar attachments_order com IDs dos anexos existentes na ordem atual
        if (newVal && Array.isArray(newVal)) {
          const attachmentIds = newVal
            .filter(att => att && att.id)
            .map(att => att.id)
          this.form.attachments_order = attachmentIds
        }
      },
      deep: true
    },

    'form.check_in_date': function(newVal) {
      this.validateSameDayCheckout()
    },

    'form.check_out_date': function(newVal) {
      this.validateSameDayCheckout()
    }
  },

  mounted() {
    this.loadData().then(() => {
      // Se já temos um stayId ao montar, tentar editar
      if (this.stayIdToEdit) {
        const booking = this.bookings.find(b => b.id === this.stayIdToEdit)
        if (booking) {
          this.editBooking(booking)
          router.params.stayId = null
        }
      }

      // Abrir modal de "Nova Reserva" ao vir da tela de Disponibilidades
      if (router.params?.openNewBooking) {
        this.openCreateModal('booking')
        router.params.openNewBooking = null
      }
    })
  },

  methods: {
    getEmptyForm(type = 'booking') {
      return {
        customer_id: '',
        property_id: '',
        seller_id: '',
        service_type_id: '',
        check_in_date: '',
        check_in_time: type === 'booking' ? '14:00' : '',
        check_out_date: '',
        check_out_time: type === 'booking' ? '12:00' : '',
        number_of_guests: 1,
        total_due: '0,00',
        deposit_amount: '0,00',
        final_amount: '0,00',
        guest_note: '',
        total_payable: '0,00',
        total_paid: '0,00',
        seller_note: '',
        payment_conditions: '',
        host_stars: null,
        host_comment: '',
        description: '',
        attachments: [],
        remove_attachment_ids: [],
        attachments_order: []
      }
    },

    async loadData() {
      this.loading = true
      try {
        const { getProperties, getServiceTypes } = useApi()
        await Promise.all([
          this.loadServicesByMonth(),
          this.loadProperties(getProperties),
          this.loadServiceTypes(getServiceTypes)
        ])
      } finally {
        this.loading = false
      }
    },

    async loadServicesByMonth() {
      this.loading = true
      
      const startDate = new Date(this.currentYear, this.currentMonth, 1)
      const endDate = new Date(this.currentYear, this.currentMonth + 1, 0)

      const startDateString = startDate.toISOString().split('T')[0]
      const endDateString = endDate.toISOString().split('T')[0]

      try {
        const { getServices } = useApi()
        const { data, error } = await getServices(startDateString, endDateString)
        if (!error && data && Array.isArray(data.data)) {
          this.bookings = data.data
        }
      } catch (err) {
        console.error('Error loading bookings by month:', err)
      } finally {
        this.loading = false
      }
    },

    async loadBookings(apiMethod) {
      try {
        const { data, error } = await apiMethod()
        if (!error && data && Array.isArray(data.data)) {
          this.bookings = data.data
        }
      } catch (err) {
        console.error('Error loading bookings:', err)
      }
    },

    async loadProperties(apiMethod) {
      try {
        const { data, error } = await apiMethod()
        if (!error) {
          this.properties = Array.isArray(data?.data) ? data.data : data
          // Se há imóveis e nenhum está selecionado, selecionar o primeiro
          // (mas respeitamos o valor restaurado do localStorage quando existir).
          if (this.properties.length > 0 && (this.selectedPropertyId === null || this.selectedPropertyId === undefined || this.selectedPropertyId === '')) {
            this.selectedPropertyId = this.properties[0].id
          }
        }
      } catch (err) {
        console.error('Error loading properties:', err)
      } finally {
        this.propertiesLoaded = true
      }
    },

    async loadServiceTypes(apiMethod) {
      try {
        const { data, error } = await apiMethod()
        if (!error) this.serviceTypes = data
      } catch (err) {
        console.error('Error loading service types:', err)
      }
    },

    getBookingsForDay(date) {
      let filtered = this.bookings
      if (this.selectedPropertyId !== null && this.selectedPropertyId !== undefined) {
        filtered = filtered.filter(b => {
          const bookingPropertyId = b.property?.id || b.property_id
          return String(bookingPropertyId) === String(this.selectedPropertyId)
        })
      }
      
      // Normalizar a data do calendário para início do dia (00:00:00)
      const targetDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())
      
      return filtered.filter(booking => {
        const [checkInY, checkInM, checkInD] = booking.check_in_date.split('-').map(Number)
        const [checkOutY, checkOutM, checkOutD] = booking.check_out_date.split('-').map(Number)
        const checkIn = new Date(checkInY, checkInM - 1, checkInD)
        const checkOut = new Date(checkOutY, checkOutM - 1, checkOutD)
        
        return targetDate >= checkIn && targetDate <= checkOut
      })
    },

    getBookingColor(booking) {
      if (booking.type === 'Service') {
        return 'bg-amber-100 text-amber-800 hover:bg-amber-200'
      }
      return 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200'
    },

    getBookingLabel(booking) {
      if (booking.type === 'Service') {
        const serviceTypeName = booking.service_type?.name || 'Serviço'
        return serviceTypeName
      }
      return `${booking.customer?.phone || ''}`
    },

    getBookingTooltip(booking) {
      const propertyName = booking.property?.name || 'Imóvel'
      let tooltip = ''
      
      if (booking.type === 'Service') {
        const serviceTypeName = booking.service_type?.name || 'Serviço'
        const providerName = booking.customer?.name || 'Prestador'
        tooltip = `${serviceTypeName} - ${providerName} - ${propertyName}`
      } else {
        const customerName = booking.customer?.name || 'Hóspede'
        tooltip = `Hospedagem - ${customerName} - ${propertyName}`
      }
      
      if (booking.check_in_time || booking.check_out_time) {
        tooltip += '\n'
        if (booking.check_in_time) {
          tooltip += `Início: ${this.formatTime(booking.check_in_time)}`
        }
        if (booking.check_out_time) {
          if (booking.check_in_time) tooltip += ' | '
          tooltip += `Fim: ${this.formatTime(booking.check_out_time)}`
        }
      }
      
      return tooltip
    },

    calculateBalanceDue() {
      const total = this.parseCurrencyToNumber(this.form.total_due || '0')
      const deposit = this.parseCurrencyToNumber(this.form.deposit_amount || '0')
      const final = this.parseCurrencyToNumber(this.form.final_amount || '0')
      return Math.max(0, total - deposit - final)
    },

    calculateBalancePayable() {
      const totalPayable = this.parseCurrencyToNumber(this.form.total_payable || '0')
      const totalPaid = this.parseCurrencyToNumber(this.form.total_paid || '0')
      return Math.max(0, totalPayable - totalPaid)
    },

    formatCurrencyDisplay(value) {
      // Garantir que é um número
      const numValue = typeof value === 'string' ? parseFloat(value.replace(',', '.')) : parseFloat(value || 0)
      
      // Formatar com 2 casas decimais usando pt-BR locale
      return numValue.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },

    handleCurrencyInput(event, fieldName) {
      const cursorPosition = event.target.selectionStart
      const oldValue = event.target.value
      const oldLength = oldValue.length
      
      // Aplica a máscara
      const newValue = this.applyCurrencyMask(event.target.value)
      
      // Atualiza o form
      this.form[fieldName] = newValue
      
      // Restaura a posição do cursor após a formatação
      this.$nextTick(() => {
        const newLength = newValue.length
        const diff = newLength - oldLength
        const newCursorPosition = Math.max(0, cursorPosition + diff)
        event.target.setSelectionRange(newCursorPosition, newCursorPosition)
      })
    },

    isToday(date) {
      const today = new Date()
      return date.getDate() === today.getDate() &&
             date.getMonth() === today.getMonth() &&
             date.getFullYear() === today.getFullYear()
    },

    formatDate(dateString) {
      const [year, month, day] = dateString.split('-')
      const date = new Date(year, month - 1, day)
      return date.toLocaleDateString('pt-BR')
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return ''
      const d = new Date(dateTimeString)
      if (Number.isNaN(d.getTime())) return ''
      const date = d.toLocaleDateString('pt-BR')
      const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
      return `${date} ${time}`
    },

    hasGuestEvaluation(booking) {
      if (!booking) return false
      return !!(booking.guest_evaluated_at || booking.guest_comment || Number(booking.guest_stars || 0) > 0)
    },

    async copyGuestEvaluationLink() {
      if (!this.guestEvaluationLink) return

      try {
        await navigator.clipboard.writeText(this.guestEvaluationLink)
      } catch (e) {
        // Fallback para navegadores sem Clipboard API.
        const input = document.createElement('input')
        input.value = this.guestEvaluationLink
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
      }

      this.guestLinkCopied = true
      setTimeout(() => {
        this.guestLinkCopied = false
      }, 2000)
    },

    formatTime(timeString) {
      if (!timeString) return ''
      // timeString pode vir como "HH:MM:SS" ou "HH:MM"
      const parts = timeString.split(':')
      return `${parts[0]}:${parts[1]}`
    },

    validateSameDayCheckout() {
      // Limpar erro anterior se existir
      if (this.formErrors.sameDayCheckout) {
        delete this.formErrors.sameDayCheckout
      }

      // Se as datas forem iguais e não tiver horários, mostrar aviso
      if (this.form.check_in_date && this.form.check_out_date && 
          this.form.check_in_date === this.form.check_out_date) {
        if (!this.form.check_in_time || !this.form.check_out_time) {
          this.formErrors.sameDayCheckout = 'Atenção: Para check-in e check-out no mesmo dia, é obrigatório informar os horários'
        }
      }
    },

    previousMonth() {
      if (this.currentMonth === 0) {
        this.currentMonth = 11
        this.currentYear--
      } else {
        this.currentMonth--
      }
    },

    nextMonth() {
      if (this.currentMonth === 11) {
        this.currentMonth = 0
        this.currentYear++
      } else {
        this.currentMonth++
      }
    },

    goToToday() {
      const today = new Date()
      this.currentMonth = today.getMonth()
      this.currentYear = today.getFullYear()
    },

    navigateToProperties() {
      navigateTo('properties')
    },

    openHistoryView() {
      this.historyView = true
    },

    closeHistoryView() {
      this.historyView = false
      this.loadServicesByMonth()
    },

    refreshBookingHistoryIfOpen() {
      if (!this.historyView) return
      this.$nextTick(() => {
        this.$refs.bookingHistory?.refresh?.()
      })
    },

    openCreateModal(type = 'booking') {
      this.entryType = type
      this.editingBooking = null
      this.form = this.getEmptyForm(type)
      if (this.selectedPropertyId !== null) {
        this.form.property_id = this.selectedPropertyId
      }
      this.formErrors = {}
      // Limpar estados de pessoas selecionadas
      this.currentCustomer = null
      this.currentSeller = null
      this.lastHostEvaluation = null
      this.loadingLastHostEvaluation = false
      this.isModalOpen = true
    },

    async viewBooking(booking) {
      const latestBooking = await this.fetchBookingLatestData(booking)
      const type = latestBooking.type === 'Service' ? 'cleaning' : 'booking'
      this.entryType = type
      this.editingBooking = latestBooking
      
      // Função helper para formatar valor monetário
      const formatMoneyValue = (value) => {
        const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : parseFloat(value || 0)
        return this.formatCurrencyDisplay(numValue)
      }
      
      this.form = {
        ...this.getEmptyForm(type),
        ...latestBooking,
        customer_id: latestBooking.customer?.id ? String(latestBooking.customer.id) : (latestBooking.customer_id ? String(latestBooking.customer_id) : ''),
        property_id: latestBooking.property?.id || latestBooking.property_id || '',
        seller_id: latestBooking.seller?.id ? String(latestBooking.seller.id) : (latestBooking.seller_id ? String(latestBooking.seller_id) : ''),
        service_type_id: latestBooking.service_type?.id ? String(latestBooking.service_type.id) : (latestBooking.service_type_id ? String(latestBooking.service_type_id) : ''),
        // Formatar valores monetários para exibição
        total_due: formatMoneyValue(latestBooking.total_due),
        deposit_amount: formatMoneyValue(latestBooking.deposit_amount),
        final_amount: formatMoneyValue(latestBooking.final_amount),
        total_payable: formatMoneyValue(latestBooking.total_payable),
        total_paid: formatMoneyValue(latestBooking.total_paid)
      }
      this.normalizeHostEvaluationForm()
      this.isModalOpen = true
    },

    async editBooking(booking) {
      const latestBooking = await this.fetchBookingLatestData(booking)
      const type = latestBooking.type === 'Service' ? 'cleaning' : 'booking'
      this.entryType = type
      this.editingBooking = latestBooking
      
      // Função helper para formatar valor monetário
      const formatMoneyValue = (value) => {
        const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : parseFloat(value || 0)
        return this.formatCurrencyDisplay(numValue)
      }
      
      this.form = {
        ...this.getEmptyForm(type),
        ...latestBooking,
        customer_id: latestBooking.customer?.id ? String(latestBooking.customer.id) : (latestBooking.customer_id ? String(latestBooking.customer_id) : ''),
        property_id: latestBooking.property?.id || latestBooking.property_id || '',
        seller_id: latestBooking.seller?.id ? String(latestBooking.seller.id) : (latestBooking.seller_id ? String(latestBooking.seller_id) : ''),
        service_type_id: latestBooking.service_type?.id ? String(latestBooking.service_type.id) : (latestBooking.service_type_id ? String(latestBooking.service_type_id) : ''),
        // Formatar valores monetários para exibição
        total_due: formatMoneyValue(latestBooking.total_due),
        deposit_amount: formatMoneyValue(latestBooking.deposit_amount),
        final_amount: formatMoneyValue(latestBooking.final_amount),
        total_payable: formatMoneyValue(latestBooking.total_payable),
        total_paid: formatMoneyValue(latestBooking.total_paid),
        // Garantir que anexos e ordem sejam carregados
        attachments: latestBooking.attachments || [],
        attachments_order: latestBooking.attachments_order || []
      }
      this.normalizeHostEvaluationForm()
      // Limpar estados para forçar uso do editingBooking
      this.currentCustomer = null
      this.currentSeller = null
      this.formErrors = {}
      this.isModalOpen = true
    },

    closeModal() {
      // Liberar URLs de preview de novos arquivos (File/Blob)
      if (this.form.attachments) {
        this.form.attachments.forEach(item => {
          const isFile = (typeof File !== 'undefined' && item instanceof File) ||
            (typeof Blob !== 'undefined' && item instanceof Blob)
          if (isFile && item.previewUrl) {
            URL.revokeObjectURL(item.previewUrl)
          }
        })
      }
      
      this.isModalOpen = false
      this.editingBooking = null
      // Limpar estados de pessoas selecionadas
      this.currentCustomer = null
      this.currentSeller = null
      this.lastHostEvaluation = null
      this.loadingLastHostEvaluation = false
      this.form = this.getEmptyForm()
      this.entryType = 'booking'
      this.formErrors = {}
    },

    openSaveConfirmBooking() {
      if (this.saving) return
      this.saveConfirmBookingOpen = true
    },

    confirmSaveBooking() {
      this.saveConfirmBookingOpen = false
      this.saveBooking()
    },

    closeSaveConfirmBooking() {
      this.saveConfirmBookingOpen = false
    },

    buildServiceFormData() {
      const isCleaning = this.entryType === 'cleaning'
      this.normalizeHostEvaluationForm()
      
      // Verificar se há novos anexos (arquivos File/Blob)
      const isFile = (item) => (typeof File !== 'undefined' && item instanceof File) ||
        (typeof Blob !== 'undefined' && item instanceof Blob)
      const newFiles = (this.form.attachments || []).filter(item => isFile(item))
      const hasNewFiles = newFiles.length > 0
      const hasRemovedAttachments = this.form.remove_attachment_ids && this.form.remove_attachment_ids.length > 0
      const hasAttachmentsOrder = this.form.attachments_order && this.form.attachments_order.length > 0

      // Helper para converter IDs para números ou deixar null
      const parseId = (value) => {
        if (!value || value === '' || value === 'new') return null
        const parsed = parseInt(value, 10)
        return isNaN(parsed) ? null : parsed
      }

      // Dados do serviço/hospedagem
      const serviceData = {
        customer_id: parseId(this.form.customer_id),
        property_id: parseId(this.form.property_id),
        seller_id: parseId(this.form.seller_id),
        service_type_id: parseId(this.form.service_type_id),
        check_in_date: this.form.check_in_date,
        check_in_time: this.form.check_in_time || null,
        check_out_date: this.form.check_out_date,
        check_out_time: this.form.check_out_time || null,
        number_of_guests: this.form.number_of_guests,
        total_due: this.parseCurrencyToNumber(this.form.total_due || '0'),
        deposit_amount: this.parseCurrencyToNumber(this.form.deposit_amount || '0'),
        final_amount: this.parseCurrencyToNumber(this.form.final_amount || '0'),
        total_payable: this.parseCurrencyToNumber(this.form.total_payable || '0'),
        total_paid: this.parseCurrencyToNumber(this.form.total_paid || '0'),
        balance_due: this.calculateBalanceDue(),
        balance_payable: this.calculateBalancePayable(),
        guest_note: this.form.guest_note || '',
        payment_conditions: this.form.payment_conditions || '',
        seller_note: this.form.seller_note || '',
        host_stars: this.form.host_stars ? Number(this.form.host_stars) : null,
        host_comment: this.form.host_comment || '',
        description: this.form.description || ''
      }

      // Determinar o prefixo baseado no tipo
      const paramKey = isCleaning ? 'service' : 'stay'

      // Se vamos usar JSON (não FormData), adicionar arrays ao serviceData
      if (!hasNewFiles && !hasRemovedAttachments && !hasAttachmentsOrder) {
        return { useFormData: false, payload: serviceData, paramKey }
      }

      // Para JSON, incluir arrays apenas se tiverem elementos
      if (!hasNewFiles) {
        if (hasRemovedAttachments) {
          serviceData.remove_attachment_ids = this.form.remove_attachment_ids
        }
        if (hasAttachmentsOrder) {
          serviceData.attachments_order = this.form.attachments_order
        }
        return { useFormData: false, payload: serviceData, paramKey }
      }

      // Construir FormData para enviar arquivos
      const formData = new FormData()
      
      // Adicionar todos os campos do serviço/hospedagem com prefixo correto
      Object.keys(serviceData).forEach(key => {
        const value = serviceData[key]
        if (value !== null && value !== undefined && value !== '') {
          formData.append(`${paramKey}[${key}]`, value)
        }
      })

      // Adicionar novos arquivos
      newFiles.forEach((file) => {
        formData.append(`${paramKey}[attachments][]`, file, file.name)
      })

      // Adicionar IDs de anexos a remover
      if (hasRemovedAttachments) {
        this.form.remove_attachment_ids.forEach(id => {
          formData.append(`${paramKey}[remove_attachment_ids][]`, id)
        })
      }

      // Adicionar ordem dos anexos
      if (this.form.attachments_order && this.form.attachments_order.length > 0) {
        this.form.attachments_order.forEach(id => {
          formData.append(`${paramKey}[attachments_order][]`, id)
        })
      }

      return { useFormData: true, payload: formData, paramKey }
    },

    async saveBooking() {
      this.saving = true
      this.formErrors = {}

      // Validar horários se check-in e check-out forem no mesmo dia
      if (this.form.check_in_date === this.form.check_out_date) {
        if (!this.form.check_in_time || !this.form.check_out_time) {
          this.formErrors.general = 'Para check-in e check-out no mesmo dia, é necessário definir os horários'
          this.saving = false
          return
        }

        if (this.form.check_out_time <= this.form.check_in_time) {
          this.formErrors.general = 'O horário de saída deve ser posterior ao horário de entrada quando no mesmo dia'
          this.saving = false
          return
        }
      }

      try {
        const { post, put, postFormData, putFormData } = useApi()
        const { useFormData, payload, paramKey } = this.buildServiceFormData()
        
        // Determinar o tipo de endpoint baseado no tipo de entrada
        const isService = this.entryType === 'cleaning'
        const endpoint = isService ? '/api/v1/services' : '/api/v1/stays'

        let result
        if (this.editingBooking) {
          if (useFormData) {
            result = await putFormData(`${endpoint}/${this.editingBooking.id}`, payload)
          } else {
            result = await put(`${endpoint}/${this.editingBooking.id}`, { [paramKey]: payload })
          }
        } else {
          if (useFormData) {
            result = await postFormData(endpoint, payload)
          } else {
            result = await post(endpoint, { [paramKey]: payload })
          }
        }

        if (result.error) {
          this.formErrors.general = result.error.response?.data?.errors?.join(', ') || 'Erro ao salvar reserva'
          return
        }

        // Salvar o imóvel confirmado no localStorage (para reaparecer selecionado ao alternar telas).
        const masterCode = localStorage.getItem('masterCode') || 'default'
        const lastPropertyKey = `tripday:last_selected_property_id:${masterCode}`
        const confirmedPropertyId =
          result?.data?.property?.id ||
          result?.data?.property_id ||
          this.form.property_id

        let shouldReloadAfterSave = true
        if (confirmedPropertyId !== null && confirmedPropertyId !== undefined && String(confirmedPropertyId) !== '') {
          const confirmedPropertyIdStr = String(confirmedPropertyId)
          localStorage.setItem(lastPropertyKey, String(confirmedPropertyId))

          // Manter o select2 correto na tela (próximo "Nova Reserva") quando o backend retornar algo diferente.
          if (String(this.selectedPropertyId) !== confirmedPropertyIdStr) {
            this.selectedPropertyId = confirmedPropertyIdStr
            shouldReloadAfterSave = false // watcher já recarrega as reservas
          }
        }

        this.closeModal()
        if (this.historyView) {
          this.refreshBookingHistoryIfOpen()
        } else if (shouldReloadAfterSave) {
          this.loadServicesByMonth()
        }
      } catch (err) {
        this.formErrors.general = err.response?.data?.errors?.join(', ') || 'Erro ao salvar reserva'
      } finally {
        this.saving = false
      }
    },

    confirmDelete(booking) {
      this.deleteConfirmBooking = booking
    },

    handleCustomerChange(value) {
      if (value === 'new') {
        this.quickPersonModalOpen = { isOpen: true, type: 'Customer' }
        this.form.customer_id = ''
        this.currentCustomer = null
        this.lastHostEvaluation = null
        this.loadingLastHostEvaluation = false
      } else {
        // Buscar o objeto completo da pessoa selecionada
        this.fetchPersonById(value, 'customer')
        this.fetchLastHostEvaluation(value)
      }
    },

    handleProviderChange(value) {
      if (value === 'new') {
        this.quickPersonModalOpen = { isOpen: true, type: 'Provider' }
        this.form.customer_id = ''
        this.currentCustomer = null
      } else {
        // Buscar o objeto completo da pessoa selecionada
        this.fetchPersonById(value, 'provider')
      }
    },

    handleSellerChange(value) {
      if (value === 'new') {
        this.quickPersonModalOpen = { isOpen: true, type: 'Seller' }
        this.form.seller_id = ''
        this.currentSeller = null
      } else {
        // Buscar o objeto completo da pessoa selecionada
        this.fetchPersonById(value, 'agent')
      }
    },

    async fetchPersonById(personId, role) {
      if (!personId) return
      
      try {
        const userToken = localStorage.getItem('userToken')
        const tenantToken = localStorage.getItem('tenantToken')
        
        const response = await axios.get(`/api/v1/people/${personId}`, {
          headers: { 
            Authorization: `Bearer ${userToken}`, 
            'Tenant-Authorization': `Bearer ${tenantToken}` 
          }
        })
        
        if (response.data) {
          // Atualizar o estado correto baseado no role
          if (role === 'customer' || role === 'provider') {
            this.currentCustomer = response.data
          } else if (role === 'agent') {
            this.currentSeller = response.data
          }
        }
      } catch (err) {
        console.error('Error fetching person:', err)
      }
    },

    async fetchBookingLatestData(booking) {
      if (!booking?.id) return booking

      const isService = booking.type === 'Service'
      const endpoint = isService ? `/api/v1/services/${booking.id}` : `/api/v1/stays/${booking.id}`

      try {
        const userToken = localStorage.getItem('userToken')
        const tenantToken = localStorage.getItem('tenantToken')

        const response = await axios.get(endpoint, {
          headers: {
            Authorization: `Bearer ${userToken}`,
            'Tenant-Authorization': `Bearer ${tenantToken}`
          }
        })

        return response.data || booking
      } catch (err) {
        console.error('Error loading latest booking data:', err)
        return booking
      }
    },

    handleQuickPersonSave(person) {
      // Apenas atualizar o ID do formulário, o PersonSelectAsync vai lidar com a atualização
      if (this.quickPersonModalOpen.type === 'Customer') {
        this.form.customer_id = String(person.id)
        this.fetchLastHostEvaluation(person.id)
      } else if (this.quickPersonModalOpen.type === 'Provider') {
        this.form.customer_id = String(person.id)
      } else if (this.quickPersonModalOpen.type === 'Seller') {
        this.form.seller_id = String(person.id)
      }
      this.closeQuickPersonModal()
    },

    closeQuickPersonModal() {
      this.quickPersonModalOpen = { isOpen: false, type: null }
    },

    async fetchLastHostEvaluation(customerId) {
      if (this.entryType !== 'booking' || this.editingBooking) return
      if (!customerId || customerId === 'new') {
        this.lastHostEvaluation = null
        this.loadingLastHostEvaluation = false
        return
      }

      this.loadingLastHostEvaluation = true
      try {
        const userToken = localStorage.getItem('userToken')
        const tenantToken = localStorage.getItem('tenantToken')
        const response = await axios.get('/api/v1/stays/last_host_evaluation', {
          params: { customer_id: customerId },
          headers: {
            Authorization: `Bearer ${userToken}`,
            'Tenant-Authorization': `Bearer ${tenantToken}`
          }
        })
        this.lastHostEvaluation = response?.data?.data || null
      } catch (err) {
        console.error('Error loading last host evaluation:', err)
        this.lastHostEvaluation = null
      } finally {
        this.loadingLastHostEvaluation = false
      }
    },

    handleServiceTypeChange(value) {
      if (value === 'new') {
        this.quickServiceTypeModalOpen = true
        this.form.service_type_id = ''
      }
    },

    handleQuickServiceTypeSave(serviceType) {
      // Atualizar o ID e forçar recarregamento
      this.$nextTick(() => {
        this.form.service_type_id = String(serviceType.id)
        // Força o recarregamento dos tipos de serviço
        this.loadServiceTypes(useApi().getServiceTypes)
      })
      this.closeQuickServiceTypeModal()
    },

    closeQuickServiceTypeModal() {
      this.quickServiceTypeModalOpen = false
    },

    clearHostEvaluationFields() {
      this.form.host_stars = null
      this.form.host_comment = ''
      if (Object.prototype.hasOwnProperty.call(this.form, 'host_evaluated_at')) {
        this.form.host_evaluated_at = null
      }
    },

    normalizeHostEvaluationForm() {
      if (Number(this.form.host_stars || 0) > 0) return
      this.form.host_comment = ''
      if (Object.prototype.hasOwnProperty.call(this.form, 'host_evaluated_at')) {
        this.form.host_evaluated_at = null
      }
    },

    async deleteBooking() {
      this.deleting = true

      try {
        const { delete: deleteApi } = useApi()
        const isService = this.deleteConfirmBooking.type === 'Service'
        const endpoint = isService ? '/api/v1/services' : '/api/v1/stays'
        const { error } = await deleteApi(`${endpoint}/${this.deleteConfirmBooking.id}`)

        if (!error) {
          this.deleteConfirmBooking = null
          this.closeModal()
          if (this.historyView) {
            this.refreshBookingHistoryIfOpen()
          } else {
            this.loadServicesByMonth()
          }
        }
      } catch (err) {
        console.error('Error deleting booking:', err)
        this.deleteConfirmBooking = null
      } finally {
        this.deleting = false
      }
    },

    async generateContractSigning() {
      if (!this.editingBooking?.id || this.editingBooking.type === 'Service') return

      this.contractSigningGenerating = true
      this.formErrors = {}

      try {
        const { post } = useApi()
        const { data, error } = await post(`/api/v1/stays/${this.editingBooking.id}/generate_contract_signing`, {})

        if (error) {
          const res = error.response?.data
          const msgs = res?.errors
          this.formErrors.general = Array.isArray(msgs)
            ? msgs.join(' ')
            : (res?.error || 'Não foi possível preparar o link de assinatura.')
          return
        }

        if (data) {
          Object.assign(this.editingBooking, {
            contract_signing_url: data.contract_signing_url,
            contract_signing_generated_at: data.contract_signing_generated_at,
            contract_signing_link_expires_at: data.contract_signing_link_expires_at,
            contract_signed_at: data.contract_signed_at || this.editingBooking.contract_signed_at,
            contract_signing_generated_by: data.contract_signing_generated_by || this.editingBooking.contract_signing_generated_by,
            contract_signing_pdf_url: data.contract_signing_pdf_url
          })
        }
      } catch (err) {
        console.error('Error generating contract signing:', err)
        this.formErrors.general = 'Não foi possível preparar o link de assinatura.'
      } finally {
        this.contractSigningGenerating = false
      }
    },

    openContractSigningDeleteConfirm() {
      if (!this.editingBooking?.id || this.editingBooking.type === 'Service') return
      if (this.editingBooking.contract_signed_at) return
      if (!this.stayContractFinalBlockVisible) return
      this.contractSigningDeleteConfirmOpen = true
    },

    async confirmDeleteContractSigning() {
      if (!this.editingBooking?.id || this.editingBooking.type === 'Service') return
      if (this.editingBooking.contract_signed_at) return
      if (!this.stayContractFinalBlockVisible) return

      this.contractSigningCancelling = true
      this.formErrors = {}

      try {
        const { post } = useApi()
        const { data, error } = await post(`/api/v1/stays/${this.editingBooking.id}/cancel_contract_signing`, {})

        if (error) {
          const res = error.response?.data
          const msgs = res?.errors
          this.formErrors.general = Array.isArray(msgs)
            ? msgs.join(' ')
            : (res?.error || 'Não foi possível excluir o contrato gerado.')
          return
        }

        if (data) {
          Object.assign(this.editingBooking, {
            contract_signing_url: data.contract_signing_url ?? null,
            contract_signing_generated_at: data.contract_signing_generated_at ?? null,
            contract_signing_link_expires_at: data.contract_signing_link_expires_at ?? null,
            contract_signed_at: data.contract_signed_at ?? null,
            contract_signing_generated_by: data.contract_signing_generated_by ?? null,
            contract_signing_pdf_url: data.contract_signing_pdf_url ?? null
          })
        }
      } catch (err) {
        console.error('Error cancelling contract signing:', err)
        this.formErrors.general = 'Não foi possível excluir o contrato gerado.'
      } finally {
        this.contractSigningCancelling = false
        this.contractSigningDeleteConfirmOpen = false
      }
    },

    async copyContractSigningLink() {
      const url = this.editingBooking?.contract_signing_url
      if (!url) return

      try {
        await navigator.clipboard.writeText(url)
        this.contractSigningLinkCopied = true
        window.setTimeout(() => {
          this.contractSigningLinkCopied = false
        }, 2000)
      } catch (e) {
        const input = document.createElement('input')
        input.value = url
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
        this.contractSigningLinkCopied = true
        window.setTimeout(() => {
          this.contractSigningLinkCopied = false
        }, 2000)
      }
    },

    shareContractSigningViaWhatsApp() {
      const url = this.editingBooking?.contract_signing_url
      if (!url) return
      const text = buildWhatsAppMessageWithLink(
        'Segue o link para você analisar e assinar o contrato de locação. Clique abaixo e siga as instruções!',
        url
      )
      openWhatsAppShare(this.editingBooking?.customer?.phone, text)
    },

    shareGuestEvaluationViaWhatsApp() {
      const url = this.guestEvaluationLink
      if (!url) return
      const text = buildWhatsAppMessageWithLink(
        'Segue o link para você avaliar sua hospedagem — Sua opinião é essencial para aprimorarmos nossos serviços! Agradecemos muito!',
        url
      )
      openWhatsAppShare(this.editingBooking?.customer?.phone, text)
    },

    async exportContract() {
      if (!this.editingBooking?.id) return

      this.contractDownloading = true
      this.formErrors = {} // Limpar erros anteriores
      
      try {
        const { getHeaders } = useApi()
        const isService = this.editingBooking.type === 'Service'
        const endpoint = isService ? '/api/v1/services' : '/api/v1/stays'
        const requestUrl = `${window.location.origin}${endpoint}/${this.editingBooking.id}/contract`
        const response = await axios.get(requestUrl, {
          headers: getHeaders(),
          responseType: 'blob'
        })

        // Verificar se a resposta é JSON (erro) ou blob (sucesso)
        if (response.data.type === 'application/json') {
          // É um erro JSON
          const text = await response.data.text()
          const errorData = JSON.parse(text)
          
          // Mostrar mensagem de erro formatada
          if (errorData.missing_fields && errorData.missing_fields.length > 0) {
            this.formErrors.general = `${errorData.error}`
          } else {
            this.formErrors.general = errorData.error || 'Não foi possível exportar o contrato.'
          }
          return
        }

        // Sucesso - fazer download do arquivo
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' })
        const blobUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        const name = (this.editingBooking.customer?.name || 'documento').replace(/[\\/:*?"<>|]/g, '')

        link.href = blobUrl
        link.setAttribute('download', `Contrato-${name}.docx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(blobUrl)
      } catch (err) {
        console.error('Error exporting contract:', err)
        
        // Tentar extrair mensagem de erro do backend
        if (err.response && err.response.data) {
          try {
            // Se vier como blob, tentar ler como texto
            if (err.response.data instanceof Blob) {
              const text = await err.response.data.text()
              const errorData = JSON.parse(text)
              
              if (errorData.missing_fields && errorData.missing_fields.length > 0) {
                this.formErrors.general = `${errorData.error}`
              } else {
                this.formErrors.general = errorData.error || 'Não foi possível exportar o contrato.'
              }
            } else {
              this.formErrors.general = err.response.data.error || 'Não foi possível exportar o contrato.'
            }
          } catch (parseError) {
            this.formErrors.general = 'Não foi possível exportar o contrato. Verifique se todos os dados necessários estão preenchidos.'
          }
        } else {
          this.formErrors.general = 'Não foi possível exportar o contrato agora.'
        }
      } finally {
        this.contractDownloading = false
      }
    }
  }
}
</script>
